import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Share2, ExternalLink, X, ChevronLeft, ChevronRight, Images, BadgeCheck } from "lucide-react";
import { SocialIcon } from "@/components/ui/LinkIcon";
import { toast } from "sonner";
import InAppBrowserRedirect from "@/components/InAppBrowserRedirect";
import TelegramVipModal from "@/components/TelegramVipModal";
import LeadCaptureCard from "@/components/LeadCaptureCard";
import LinkPopupModal from "@/components/LinkPopupModal";

const PLATFORM_WORDMARKS = {
  playboy: "/icons/playboy-wordmark.png",
  mym: "/icons/mym-wordmark.png",
};

// Platforms always treated as +18 → forces external browser open from in-app webviews
const NSFW_ICONS = new Set(["onlyfans", "playboy", "mym", "mymfans", "snapchat"]);
const NSFW_URL_PATTERNS = [
  /onlyfans\.com/i,
  /playboy\.com/i,
  /mym\.fans/i,
  /mymfans/i,
  /fansly\.com/i,
];

const isNsfwLink = (link) => {
  if (!link) return false;
  if (link.nsfw === true) return true;
  if (link.icon && NSFW_ICONS.has(String(link.icon).toLowerCase())) return true;
  const url = link.url || "";
  return NSFW_URL_PATTERNS.some((re) => re.test(url));
};

const isNsfwSocialKey = (key) => NSFW_ICONS.has(String(key || "").toLowerCase());

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a4b1710a-e8aa-40f2-90ed-6aa15c96d321/artifacts/w14lowsf_Disen%CC%83o%20sin%20ti%CC%81tulo.png";

// Format number as Colombian Pesos
const formatCOP = (amount) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Use relative URL for production
const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.location.hostname.includes('preview.emergentagent.com')) {
    return process.env.REACT_APP_BACKEND_URL || '';
  }
  return '';
};

// Detect if in Instagram/Facebook in-app browser
const isInAppBrowser = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  return ua.indexOf('Instagram') > -1 || 
         ua.indexOf('FBAN') > -1 || 
         ua.indexOf('FBAV') > -1 ||
         ua.indexOf('TikTok') > -1 ||
         ua.indexOf('BytedanceWebview') > -1;
};

// Helper function to ensure URL has protocol
const ensureProtocol = (url) => {
  if (!url) return '#';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return 'https://' + url;
};

// Handle link click - try to open in external browser if in-app
const handleLinkClick = (e, url) => {
  const finalUrl = ensureProtocol(url);
  
  if (isInAppBrowser()) {
    e.preventDefault();
    
    // For Android - try intent
    if (/android/i.test(navigator.userAgent)) {
      window.location.href = `intent://${finalUrl.replace(/^https?:\/\//, '')}#Intent;scheme=https;end`;
      return;
    }
    
    // For iOS - try to force Safari
    // First attempt: x-safari URL scheme
    const safariUrl = `x-safari-${finalUrl}`;
    const link = document.createElement('a');
    link.href = safariUrl;
    link.click();
    
    // Fallback: just open the URL (will stay in in-app but at least works)
    setTimeout(() => {
      window.open(finalUrl, '_blank');
    }, 100);
  }
  // If not in-app browser, default behavior (follow the link)
};

// OnlyFans logo URL
const ONLYFANS_LOGO = "https://customer-assets.emergentagent.com/job_ed2a5928-734c-4717-9f2e-99379f5866cf/artifacts/40m5463f_image.png";

// Social platform icons mapping with colors
const socialPlatforms = [
  { key: "onlyfans", label: "OnlyFans", color: "#00AFF0", useImage: true },
  { key: "playboy", label: "Playboy", color: "#000000" },
  { key: "mym", label: "MYM", color: "#000000" },
  { key: "instagram", label: "Instagram", gradient: "linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)" },
  { key: "twitter", label: "Twitter", color: "#000000" },
  { key: "tiktok", label: "TikTok", color: "#000000" },
  { key: "telegram", label: "Telegram", color: "#0088CC" },
  { key: "youtube", label: "YouTube", color: "#FF0000" },
  { key: "snapchat", label: "Snapchat", color: "#FFFC00", textColor: "#000" },
  { key: "whatsapp", label: "WhatsApp", color: "#25D366" },
];

const CreatorProfile = () => {
  const { username } = useParams();
  const [creator, setCreator] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBranding, setShowBranding] = useState(null); // null = unknown, true = show, false = hide
  const [minLoadComplete, setMinLoadComplete] = useState(false);
  const [showVipModal, setShowVipModal] = useState(false);
  const [trm, setTrm] = useState(4200); // Default TRM fallback
  
  // Fetch TRM on mount
  useEffect(() => {
    const fetchTRM = async () => {
      try {
        const response = await fetch(
          'https://www.datos.gov.co/resource/32sa-8pi3.json?$limit=1&$order=vigenciadesde%20DESC'
        );
        const data = await response.json();
        if (data && data[0] && data[0].valor) {
          setTrm(parseFloat(data[0].valor));
        }
      } catch (error) {
        console.log('Using default TRM');
      }
    };
    fetchTRM();
  }, []);
  
  // Reset state when username changes
  useEffect(() => {
    setCreator(null);
    setIsLoading(true);
    setError(null);
    setShowBranding(null);
    setMinLoadComplete(false);
  }, [username]);
  
  // Fetch creator directly by username
  useEffect(() => {
    const controller = new AbortController();
    
    const fetchCreator = async () => {
      try {
        const response = await fetch(`${getApiUrl()}/api/creators/${username}`, {
          signal: controller.signal
        });
        if (!response.ok) {
          if (response.status === 404) {
            setCreator(null);
            setShowBranding(true); // Default for 404
          } else {
            throw new Error('Failed to fetch');
          }
        } else {
          const data = await response.json();
          setCreator(data);
          // Set branding visibility based on creator's setting
          setShowBranding(!data.hideBranding);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message);
          setCreator(null);
          setShowBranding(true); // Default for error
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    if (username) {
      fetchCreator();
    }
    
    return () => controller.abort();
  }, [username]);
  
  // Minimum loading time - only start counting after we know branding status
  useEffect(() => {
    if (showBranding !== null) {
      const timer = setTimeout(() => {
        setMinLoadComplete(true);
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [showBranding]);
  
  // Gallery lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [popupLink, setPopupLink] = useState(null);

  // Auto-show popup link once per session (controlled by `popOnLoad` flag on a link).
  // Small delay so the user can see the profile first, then the popup slides in.
  useEffect(() => {
    if (!creator?.links) return;
    const popLink = creator.links.find((l) => l.popOnLoad);
    if (!popLink) return;
    const sessionKey = `jglinks-popup-shown-${creator.username}-${popLink.id}`;
    try {
      if (sessionStorage.getItem(sessionKey)) return;
      sessionStorage.setItem(sessionKey, "1");
    } catch (e) {
      // sessionStorage not available - just show once per page load
    }
    const t = setTimeout(() => setPopupLink(popLink), 2500);
    return () => clearTimeout(t);
  }, [creator]);
  
  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };
  
  const closeLightbox = () => {
    setLightboxOpen(false);
  };
  
  const nextImage = () => {
    if (creator?.galleryImages) {
      setCurrentImageIndex((prev) => 
        prev === creator.galleryImages.length - 1 ? 0 : prev + 1
      );
    }
  };
  
  const prevImage = () => {
    if (creator?.galleryImages) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? creator.galleryImages.length - 1 : prev - 1
      );
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${creator?.name} - JGlinks`,
          text: creator?.bio,
          url: url,
        });
      } catch (err) {
        copyToClipboard(url);
      }
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("¡Enlace copiado!", {
      description: "El enlace ha sido copiado al portapapeles"
    });
  };

  // Initial loading - waiting to know branding status
  if (showBranding === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="relative w-10 h-10">
          <div className="absolute inset-0 border-2 border-white/10 rounded-full"></div>
          <div className="absolute inset-0 border-2 border-transparent border-t-white/30 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  // Loading state - Show verification screen based on branding setting
  if (!minLoadComplete) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          {/* Logo - only show if branding is enabled */}
          {showBranding && (
            <img src={LOGO_URL} alt="JGlinks" className="h-10 mx-auto mb-8 opacity-80" />
          )}
          
          {/* Loading animation */}
          <div className="relative w-16 h-16 mx-auto mb-6">
            <div className="absolute inset-0 border-2 border-white/10 rounded-full"></div>
            <div className="absolute inset-0 border-2 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
            <div className="absolute inset-2 border-2 border-transparent border-t-cyan-400 rounded-full animate-spin" style={{animationDirection: 'reverse', animationDuration: '0.8s'}}></div>
          </div>
          
          {/* Status text */}
          <p className="text-white/80 text-sm font-medium mb-2">Verificando tu navegador</p>
          <p className="text-white/40 text-xs">Esto puede tomar unos segundos...</p>
          
          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1 mt-4">
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></div>
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
          </div>
        </div>
      </div>
    );
  }

  // 404 state
  if (!creator) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-20 h-20 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">🔗</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Perfil no encontrado
          </h1>
          <p className="text-white/60 mb-6">
            El usuario @{username} no existe o no está activo.
          </p>
          <Link to="/">
            <Button variant="outline" className="rounded-full border-white/20 text-white hover:bg-white/10">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver al inicio
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Helper to check if URL is a video
  const isVideo = (url) => {
    if (!url) return false;
    return /\.(mp4|mov|webm|ogg)$/i.test(url) || url.includes('video');
  };

  // Separate featured link and regular links
  const featuredLink = creator.links?.find(l => l.featured);
  const regularLinks = creator.links?.filter(l => !l.featured) || [];
  
  // Split regular links into those with images and without
  const linksWithImages = regularLinks.filter(l => l.image);
  const linksWithoutImages = regularLinks.filter(l => !l.image);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* In-App Browser Detection Banner */}
      <InAppBrowserRedirect />
      {popupLink && (
        <LinkPopupModal
          link={popupLink}
          isVideo={isVideo}
          ensureProtocol={ensureProtocol}
          onClose={() => setPopupLink(null)}
        />
      )}
      
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{creator.name} - JGlinks | Multilink</title>
        <meta name="description" content={`${creator.name} - Perfil multilink. ${creator.bio || 'Descubre su contenido exclusivo, redes sociales y más en JGlinks.'}`} />
        <meta property="og:title" content={`${creator.name} - JGlinks`} />
        <meta property="og:description" content={creator.bio || `Perfil de ${creator.name} en JGlinks. Links, galería y contenido exclusivo.`} />
        <meta property="og:image" content={creator.avatar || creator.coverImage || ''} />
        <meta property="og:url" content={`https://jglinks.co/${creator.username}`} />
        <meta property="og:type" content="profile" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${creator.name} - JGlinks`} />
        <meta name="twitter:image" content={creator.avatar || creator.coverImage || ''} />
        <link rel="canonical" href={`https://jglinks.co/${creator.username}`} />
      </Helmet>

      {/* Hero Section with Cover Image */}
      <div className="relative">
        {/* Cover Image/Video */}
        <div className="relative h-[50vh] min-h-[400px] overflow-hidden">
          {(creator.coverVideo || isVideo(creator.coverImage)) ? (
            <video
              src={creator.coverVideo || creator.coverImage}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover object-top"
            />
          ) : (
            <img
              src={creator.coverImage || creator.avatar}
              alt={creator.name}
              loading="eager"
              className="w-full h-full object-cover object-top"
            />
          )}
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          
          {/* Top Bar */}
          <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-10">
            {/* Logo - Only show if branding is visible */}
            {showBranding ? (
              <Link to="/" className="text-white/80 hover:text-white transition-colors">
                <img src={LOGO_URL} alt="JG Models Agency" className="h-8 w-auto" />
              </Link>
            ) : (
              <div /> 
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleShare}
              className="text-white/80 hover:text-white hover:bg-white/10"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Compartir
            </Button>
          </div>
        </div>

        {/* Profile Info - Overlapping the cover */}
        <div className="relative -mt-32 px-4 pb-6 z-10">
          <div className="max-w-lg mx-auto text-center">
            {/* Name with Verified Badge */}
            <div className="flex items-center justify-center gap-2 mb-1">
              <h1 className="text-3xl sm:text-4xl font-bold text-white">
                {creator.name}
              </h1>
              {creator.featured && (
                <svg viewBox="0 0 24 24" className="w-7 h-7 flex-shrink-0" data-testid="verified-badge">
                  <path d="M23 12l-2.44-2.78.34-3.68-3.61-.82L15.4 1.54 12 3 8.6 1.54 6.71 4.72l-3.61.81.34 3.68L1 12l2.44 2.78-.34 3.69 3.61.82 1.89 3.18L12 21l3.4 1.46 1.89-3.18 3.61-.82-.34-3.68z" fill="#0095F6" />
                  <path d="M10 17l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9z" fill="white" />
                </svg>
              )}
            </div>
            <p className="text-white/60 mb-4">@{creator.username}</p>

            {/* Social Icons */}
            {creator.socialLinks && Object.keys(creator.socialLinks).length > 0 && (
              <div className="flex items-center justify-center gap-3 mb-4">
                {socialPlatforms.map((platform, idx) => {
                  const url = creator.socialLinks[platform.key];
                  if (!url) return null;
                  
                  const bgStyle = platform.key === 'onlyfans' 
                    ? { background: '#00AFF0' }
                    : { background: platform.gradient || platform.color || '#333', color: platform.textColor || '#fff' };
                  
                  return (
                    <a
                      key={platform.key}
                      href={ensureProtocol(url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-nsfw={isNsfwSocialKey(platform.key) ? "true" : "false"}
                      className="w-11 h-11 rounded-full flex items-center justify-center animate-[fadeInBounce_0.5s_ease_forwards] opacity-0 hover:scale-125 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] hover:-translate-y-1 transition-all duration-300"
                      style={{ 
                        ...bgStyle,
                        animationDelay: `${idx * 0.1}s`
                      }}
                      title={platform.label}
                    >
                      <SocialIcon platform={platform.key} className={`${platform.key === 'onlyfans' ? 'w-6 h-6' : 'w-5 h-5'} text-white`} />
                    </a>
                  );
                })}
              </div>
            )}

            {/* Bio */}
            <p className="text-white/70 text-sm sm:text-base leading-relaxed max-w-md mx-auto">
              {creator.bio}
            </p>
          </div>
        </div>
      </div>

      {/* Links Section */}
      <div className="px-4 pb-12">
        <div className="max-w-lg mx-auto">
          {/* Featured Link - Large Card */}
          {featuredLink && (
            <a
              href={ensureProtocol(featuredLink.url)}
              onClick={(e) => handleLinkClick(e, featuredLink.url)}
              target="_blank"
              rel="noopener noreferrer"
              data-nsfw={isNsfwLink(featuredLink) ? "true" : "false"}
              className="block mb-4 group animate-featured-bounce"
            >
              <div className={`relative rounded-2xl overflow-hidden bg-white/5 ${
                featuredLink.imageDisplay === 'vertical' ? 'aspect-[3/4]' :
                featuredLink.imageDisplay === 'horizontal' ? 'aspect-video' :
                'aspect-[4/3]'
              }`}>
                {(featuredLink.video || isVideo(featuredLink.image)) ? (
                  <video
                    src={featuredLink.video || featuredLink.image}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : featuredLink.image ? (
                  <img
                    src={featuredLink.image}
                    alt={featuredLink.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-white/10 to-white/5" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                {/* Badge (oferta) */}
                {featuredLink.badge && featuredLink.badge.trim() && (
                  <div className="absolute top-3 right-3 z-10 pointer-events-none">
                    <div
                      className="relative overflow-hidden px-3 py-1.5 rounded-full backdrop-blur-md shadow-md ring-1 ring-white/15 animate-badge-fade"
                      style={{
                        backgroundColor: featuredLink.bgColor || '#1a1a1a',
                        color: featuredLink.textColor || '#ffffff',
                      }}
                    >
                      <span className="relative z-10 font-semibold text-[11px] tracking-[0.08em] uppercase whitespace-nowrap">
                        {featuredLink.badge}
                      </span>
                      <span className="badge-shimmer" aria-hidden />
                    </div>
                  </div>
                )}

                {/* Platform Icon - only when NOT using wordmark */}
                {!PLATFORM_WORDMARKS[featuredLink.icon] && (
                  <div className="absolute top-4 left-4">
                    <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <SocialIcon platform={featuredLink.icon || "globe"} className="w-5 h-5 text-white" />
                    </div>
                  </div>
                )}

                {/* Title - wordmark or text */}
                <div className="absolute bottom-4 left-4 right-4">
                  {PLATFORM_WORDMARKS[featuredLink.icon] ? (
                    <img
                      src={PLATFORM_WORDMARKS[featuredLink.icon]}
                      alt={featuredLink.title}
                      className="h-9 sm:h-10 w-auto max-w-[70%] object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]"
                    />
                  ) : (
                    <h3 className="text-xl font-semibold text-white">{featuredLink.title}</h3>
                  )}
                </div>
              </div>
            </a>
          )}

          {/* Grid of Links with Images */}
          {linksWithImages.length > 0 && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              {linksWithImages.map((link) => (
                <a
                  key={link.id}
                  href={ensureProtocol(link.url)}
                  onClick={(e) => handleLinkClick(e, link.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-nsfw={isNsfwLink(link) ? "true" : "false"}
                  className={`block group ${
                    link.imageDisplay === 'vertical' ? 'row-span-2' : ''
                  }`}
                >
                  <div className={`relative rounded-xl overflow-hidden bg-white/5 h-full ${
                    link.imageDisplay === 'vertical' ? 'aspect-[3/4]' :
                    link.imageDisplay === 'horizontal' ? 'aspect-video' :
                    'aspect-square'
                  }`}>
                    {(link.video || isVideo(link.image)) ? (
                      <video
                        src={link.video || link.image}
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <img
                        src={link.image}
                        alt={link.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    
                    {/* Platform Icon */}
                    <div className="absolute top-3 left-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        link.icon === 'instagram' ? 'bg-gradient-to-tr from-yellow-500 via-pink-500 to-purple-500' :
                        link.icon === 'tiktok' ? 'bg-black border border-white/20' :
                        link.icon === 'send' ? 'bg-blue-500' :
                        link.icon === 'twitter' ? 'bg-black' :
                        'bg-white/20'
                      }`}>
                        <SocialIcon platform={link.icon} className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    
                    {/* Title */}
                    <div className="absolute bottom-3 left-3 right-3">
                      <h3 className="text-sm font-medium text-white truncate">{link.title}</h3>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}

          {/* Simple Links without Images */}
          {linksWithoutImages.length > 0 && (
            <div className="space-y-2">
              {linksWithoutImages.map((link) => (
                <a
                  key={link.id}
                  href={ensureProtocol(link.url)}
                  onClick={(e) => handleLinkClick(e, link.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-nsfw={isNsfwLink(link) ? "true" : "false"}
                  className="flex items-center gap-3 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors group"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    link.icon === 'instagram' ? 'bg-gradient-to-tr from-yellow-500 via-pink-500 to-purple-500' :
                    link.icon === 'tiktok' ? 'bg-black border border-white/20' :
                    link.icon === 'send' ? 'bg-blue-500' :
                    link.icon === 'twitter' ? 'bg-black border border-white/20' :
                    'bg-white/10'
                  }`}>
                    <SocialIcon platform={link.icon} className="w-5 h-5 text-white" />
                  </div>
                  <span className="flex-1 font-medium text-white">{link.title}</span>
                  <ExternalLink className="w-4 h-4 text-white/40 group-hover:text-white/60 transition-colors" />
                </a>
              ))}
            </div>
          )}

          {/* Telegram VIP Button */}
          {creator.telegramVip?.enabled && (
            <button
              onClick={() => setShowVipModal(true)}
              className="w-full mt-4 p-4 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <p className="font-semibold text-white">Telegram VIP</p>
                  <p className="text-white/70 text-sm">Contenido exclusivo</p>
                </div>
                <div className="text-right">
                  {creator.telegramVip.offerEnabled && creator.telegramVip.offerEndDate && new Date(creator.telegramVip.offerEndDate) > new Date() && creator.telegramVip.offerMonthlyPrice > 0 && creator.telegramVip.offerMonthlyPrice < creator.telegramVip.monthlyPrice ? (
                    <>
                      <p className="text-white/40 text-xs line-through">{formatCOP(creator.telegramVip.monthlyPrice * trm)}</p>
                      <p className="text-white font-bold">{formatCOP(creator.telegramVip.offerMonthlyPrice * trm)}</p>
                      <p className="text-green-300 text-xs font-medium">mensual</p>
                    </>
                  ) : (
                    <>
                      <p className="text-white font-bold">{formatCOP(creator.telegramVip.monthlyPrice * trm)}</p>
                      <p className="text-white/60 text-xs">mensual</p>
                    </>
                  )}
                </div>
              </div>
            </button>
          )}

          {/* Public Gallery Section */}
          {creator.galleryImages && creator.galleryImages.length > 0 && (
            <div className="mt-8">
              <div className="flex items-center gap-2 mb-4">
                <Images className="w-5 h-5 text-white/60" />
                <h2 className="text-lg font-semibold text-white">Galería</h2>
                <span className="text-white/40 text-sm">({creator.galleryImages.length})</span>
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                {creator.galleryImages.map((img, index) => (
                  <button
                    key={img.id}
                    onClick={() => openLightbox(index)}
                    className="relative aspect-square rounded-lg overflow-hidden group cursor-pointer"
                  >
                    {img.type === 'video' ? (
                      <>
                        <video 
                          src={img.url} 
                          muted 
                          autoPlay 
                          loop 
                          playsInline
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        <div className="absolute bottom-1 left-1 bg-black/60 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] text-white font-medium">VIDEO</div>
                      </>
                    ) : (
                      <img 
                        src={img.url} 
                        alt={`Foto ${index + 1}`}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* +18 Notice */}
          <div className="mt-8 text-center">
            <p className="text-white/40 text-xs mb-4">Contenido exclusivo +18</p>
          </div>

          {/* JGFans — exclusive subscription feed */}
          {creator.jgfansEnabled && (
            <Link
              to={`/jgfans/${creator.username}`}
              data-testid="jgfans-cta-card"
              className="block mb-4 group relative overflow-hidden rounded-2xl border border-pink-500/40 bg-gradient-to-br from-pink-500/15 via-purple-500/10 to-pink-500/15 hover:from-pink-500/25 hover:to-purple-500/20 transition-all"
            >
              <div className="relative p-4 flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-pink-500/30">
                  <svg viewBox="0 0 24 24" fill="white" className="w-6 h-6">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[2px] text-pink-300 font-bold">JGFans · VIP</p>
                  <p className="font-bold text-white">Mi feed privado</p>
                  <p className="text-xs text-white/60 truncate">
                    Suscripción mensual · ${(creator.subscriptionPriceCOP ?? 35000).toLocaleString("es-CO")} COP
                  </p>
                </div>
                <div className="text-pink-300 text-xl group-hover:translate-x-1 transition-transform">→</div>
              </div>
            </Link>
          )}

          {/* Newsletter / Lead capture CTA - Subtle, footer-style.
              Only show on creators that keep the JGlinks branding visible. */}
          {creator.leadCapture?.enabled && showBranding && (
            <LeadCaptureCard
              creatorUsername={creator.username}
              config={creator.leadCapture}
            />
          )}

          {/* Subtle CTA - Only show if branding is visible */}
          {showBranding && (
            <div className="mt-6 pt-6 border-t border-white/5">
              <Link 
                to="/modelo/registro"
                className="block text-center group"
              >
                <p className="text-white/40 text-xs mb-1">¿Eres creadora?</p>
                <p className="text-white/60 text-sm group-hover:text-cyan-400 transition-colors">
                  Crea tu perfil gratis →
                </p>
              </Link>
            </div>
          )}

          {/* Branding - Only show if branding is visible */}
          {showBranding && (
            <div className="mt-4 text-center">
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-white/60 transition-colors"
              >
                <span>JGlinks</span>
              </Link>
            </div>
          )}
        </div>
      </div>
      
      {/* Lightbox Modal */}
      {lightboxOpen && creator.galleryImages && creator.galleryImages.length > 0 && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button 
            onClick={closeLightbox}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-50"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Previous Button */}
          {creator.galleryImages.length > 1 && (
            <button 
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}
          
          {/* Image or Video */}
          {creator.galleryImages[currentImageIndex]?.type === 'video' ? (
            <video 
              src={creator.galleryImages[currentImageIndex]?.url}
              autoPlay
              muted
              loop
              playsInline
              controls
              className="max-w-[90vw] max-h-[85vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img 
              src={creator.galleryImages[currentImageIndex]?.url}
              alt={`Foto ${currentImageIndex + 1}`}
              className="max-w-[90vw] max-h-[85vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          )}
          
          {/* Next Button */}
          {creator.galleryImages.length > 1 && (
            <button 
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}
          
          {/* Image Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white/10 text-white text-sm">
            {currentImageIndex + 1} / {creator.galleryImages.length}
          </div>
        </div>
      )}

      {/* Telegram VIP Modal */}
      {showVipModal && creator && (
        <TelegramVipModal 
          creator={creator} 
          onClose={() => setShowVipModal(false)} 
        />
      )}
    </div>
  );
};

export default CreatorProfile;
